/**
 * "More about this project" on the project Home (design "A calmer project Home", board 4): closed by default,
 * remembered open or closed per person in this browser. Inside: How it fits together (the services by layer, the
 * technologies, the API's base paths), Brain (its folders) beside the latest Conversations (all of them in the Sessions
 * tab), Restore points (the latest few, "Open restore points" to the Data tab's view, "Save a restore point" for an
 * editor; only when the project has datastores and its sandbox keeps restore points), then AI used (AiProvenanceCard) on
 * its own full row, since it is much taller than the other two. A part with nothing to show is left out, and More with
 * them all.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CaretRight } from '@phosphor-icons/react';
import AiProvenanceCard from '../dashboard/AiProvenanceCard';
import { formatRelativeTime } from '../../utils/relativeTime';
import { moreParts, moreRemembered, rememberMore } from '../../utils/homeModel';
import { openHistory } from '../../utils/workspaceModel';
import { usePlanTab } from '../../hooks/usePlanFeatures';
import { getOverview, listRestorePoints } from '../../services/DataService';
import { absoluteTime, pointKindLabel } from '../../utils/restorePointsModel';
import { PointKindIcon, SaveRestorePointDialog } from '../data/RestorePoints';
import '../data/Data.css';

const SHOWN_CONVERSATIONS = 3;
const SHOWN_POINTS = 3;

/**
 * The project's restore points for Home: `{ shown, points, reload }`; `shown` false while loading, when the sandbox
 * cannot keep them (`available: false`, an error) or when the project has no datastore to keep (no point and no store
 * but Kafka, which is never in a point).
 */
export function useRestorePointsSummary(projectId, enabled) {
  const [state, setState] = useState({ shown: false, points: [] });
  const reload = useCallback(async () => {
    if (!enabled || !projectId) { setState({ shown: false, points: [] }); return; }
    try {
      const answer = await listRestorePoints(projectId);
      if (!answer?.available) { setState({ shown: false, points: [] }); return; }
      const points = answer.points || [];
      if (points.length > 0) { setState({ shown: true, points }); return; }
      const overview = await getOverview(projectId).catch(() => null);
      const stores = (overview?.stores || []).filter((store) => store.kind !== 'kafka');
      setState({ shown: stores.length > 0, points: [] });
    } catch {
      setState({ shown: false, points: [] });
    }
  }, [projectId, enabled]);
  useEffect(() => { reload(); }, [reload]);
  return { ...state, reload };
}

function RestorePointsPart({ points, canEdit, projectId, onSaved }) {
  const [saving, setSaving] = useState(false);
  const shows = usePlanTab();
  return (
    <div className="ph-sub" data-testid="home-restore-points">
      <div className="ph-sub-head">
        <h3>Restore points</h3>
        <span className="ph-muted ph-small">The project’s data at earlier times, to go back to</span>
      </div>
      {points.length > 0 ? (
        <ul className="ph-list">
          {points.slice(0, SHOWN_POINTS).map((point) => (
            <li className="ph-list-row is-static" data-testid="home-restore-point" key={point.id}>
              <span className="ph-list-main"><PointKindIcon kind={point.kind} size={14} /> {pointKindLabel(point.kind)}</span>
              <span className="ph-muted ph-small" title={absoluteTime(point.createdAt)}>{formatRelativeTime(point.createdAt)}</span>
            </li>
          ))}
        </ul>
      ) : <p className="ph-muted ph-small">None yet: the first hourly one is taken within the hour.</p>}
      <div className="ph-restore-actions">
        {shows('data') && <button className="ph-btn" onClick={() => openHistory({ tab: 'data', focus: { view: 'restore' } })} type="button">Open restore points</button>}
        {canEdit && <button className="ph-btn" onClick={() => setSaving(true)} type="button">Save a restore point</button>}
      </div>
      {saving && <SaveRestorePointDialog onClose={() => setSaving(false)} onSaved={onSaved} projectId={projectId} />}
    </div>
  );
}

RestorePointsPart.propTypes = { canEdit: PropTypes.bool, onSaved: PropTypes.func, points: PropTypes.array.isRequired, projectId: PropTypes.string.isRequired };

function FitsTogether({ map, technologies, endpoints, onOpenService }) {
  return (
    <div className="ph-sub" data-testid="home-map">
      <div className="ph-sub-head">
        <h3>How it fits together</h3>
        <span className="ph-muted ph-small">{technologies.length > 0 ? technologies.slice(0, 8).join(' · ') : 'Click a service to open it'}</span>
      </div>
      <div className="pd-layers" style={{ '--cols': map.length }}>
        {map.map((column) => (
          <div className="pd-layer" key={column.id}>
            <h4><span>{column.title}</span><span>{column.nodes.length}</span></h4>
            {column.nodes.map((node) => (column.id === 'data' ? (
              <div className={`pd-node pd-node-${column.id}`} key={node.key}>
                <span className="pd-node-name">{node.label}</span>
              </div>
            ) : (
              <button className={`pd-node pd-node-${column.id}`} key={node.key} onClick={() => onOpenService(node.key, node.service)} type="button">
                <span className="pd-node-name">{node.label}</span>
                <span className="pd-node-open">Open →</span>
              </button>
            )))}
            {column.id === 'backend' && endpoints.length > 0 && (
              <span className="ph-mono ph-muted" data-testid="home-endpoints">{endpoints.join(' · ')}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

FitsTogether.propTypes = {
  endpoints: PropTypes.arrayOf(PropTypes.string).isRequired,
  map: PropTypes.array.isRequired,
  onOpenService: PropTypes.func.isRequired,
  technologies: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const MoreAboutProject = forwardRef(({
  personId, projectId, currentUserSub, map = null, technologies = [], endpoints = [], showAi = false,
  conversations = [], onOpenService, restorePoints = false, canEdit = false, refreshKey = 0,
}, ref) => {
  const [open, setOpenState] = useState(() => moreRemembered(personId));
  const setOpen = (next) => {
    setOpenState(next);
    rememberMore(personId, next);
  };
  // "See AI used" in What changed opens More and brings AI used into view.
  useImperativeHandle(ref, () => ({
    showAi: () => {
      setOpen(true);
      setTimeout(() => document.getElementById('ph-more-ai')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0);
    },
  }));

  const restore = useRestorePointsSummary(projectId, restorePoints);
  const shows = usePlanTab();
  // The Home refreshed (live, or its Refresh button): the restore points too.
  const firstKey = useRef(refreshKey);
  const reloadRestore = restore.reload;
  useEffect(() => { if (refreshKey !== firstKey.current) { reloadRestore(); } }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const parts = moreParts({ map: Boolean(map), ai: showAi, conversations: conversations.length > 0, restore: restore.shown });
  if (parts.length === 0) { return null; }
  return (
    <section aria-label="More about this project" className={`ph-card ph-more-card${open ? ' is-open' : ''}`} data-testid="home-more">
      <button aria-controls="ph-more-body" aria-expanded={open} className="ph-more-toggle" onClick={() => setOpen(!open)} type="button">
        <CaretRight aria-hidden className="ph-more-caret" size={14} weight="bold" />
        <strong>More about this project</strong>
        <span className="ph-muted ph-small">{parts.join(' · ')}</span>
      </button>
      {open && (
        <div className="ph-more-body" id="ph-more-body">
          {map && <FitsTogether endpoints={endpoints} map={map} onOpenService={onOpenService} technologies={technologies} />}
          {conversations.length > 0 && (
            <div className="ph-more-pair">
              {conversations.length > 0 && (
                <div className="ph-sub" data-testid="home-conversations">
                  <div className="ph-sub-head">
                    <h3>Conversations</h3>
                    <span className="ph-muted ph-small">The {Math.min(SHOWN_CONVERSATIONS, conversations.length)} latest; all of them are in Sessions</span>
                  </div>
                  <ul className="ph-list">
                    {conversations.slice(0, SHOWN_CONVERSATIONS).map((session) => (
                      <li className="ph-list-row is-static" key={session.id}>
                        <span className="ph-list-main">{session.name || 'Untitled conversation'}</span>
                        <span className="ph-muted ph-small">{formatRelativeTime(session.lastUpdateTime < 1e12 ? session.lastUpdateTime * 1000 : session.lastUpdateTime)}</span>
                      </li>
                    ))}
                  </ul>
                  {shows('sessions') && <button className="ph-link ph-link-start" onClick={() => openHistory({ tab: 'sessions' })} type="button">All in Sessions</button>}
                </div>
              )}
            </div>
          )}
          {restore.shown && <RestorePointsPart canEdit={canEdit} onSaved={restore.reload} points={restore.points} projectId={projectId} />}
          {showAi && (
            <div className="ph-sub ph-sub-ai" id="ph-more-ai">
              <AiProvenanceCard currentUserSub={currentUserSub} key={projectId} projectId={projectId} />
            </div>
          )}
        </div>
      )}
    </section>
  );
});

MoreAboutProject.displayName = 'MoreAboutProject';

MoreAboutProject.propTypes = {
  // An editor: "Save a restore point" too.
  canEdit: PropTypes.bool,
  conversations: PropTypes.array,
  currentUserSub: PropTypes.string,
  endpoints: PropTypes.arrayOf(PropTypes.string),
  map: PropTypes.array,
  onOpenService: PropTypes.func.isRequired,
  personId: PropTypes.string,
  projectId: PropTypes.string.isRequired,
  /** Bumped when the Home refreshes: the restore points are read again. */
  refreshKey: PropTypes.number,
  // Look for the project's restore points (a vfs-backed project in the sandbox).
  restorePoints: PropTypes.bool,
  showAi: PropTypes.bool,
  technologies: PropTypes.arrayOf(PropTypes.string),
};

export default MoreAboutProject;
